/**
 * Log Capture Service
 *
 * Captures console.log and console.error calls for in-app log viewer
 * Useful for debugging on mobile devices without access to DevTools
 */

export interface LogEntry {
  id: string;
  timestamp: Date;
  level: 'log' | 'error' | 'warn';
  message: string;
  args: any[];
}

class LogCaptureService {
  private logs: LogEntry[] = [];
  private maxLogs = 500; // Keep last 500 logs
  private listeners: Set<(logs: LogEntry[]) => void> = new Set();
  private originalConsole = {
    log: console.log,
    error: console.error,
    warn: console.warn,
  };

  constructor() {
    this.interceptConsole();
  }

  private interceptConsole() {
    const self = this;

    console.log = function (...args: any[]) {
      self.captureLog('log', args);
      self.originalConsole.log.apply(console, args);
    };

    console.error = function (...args: any[]) {
      self.captureLog('error', args);
      self.originalConsole.error.apply(console, args);
    };

    console.warn = function (...args: any[]) {
      self.captureLog('warn', args);
      self.originalConsole.warn.apply(console, args);
    };
  }

  private captureLog(level: 'log' | 'error' | 'warn', args: any[]) {
    const message = args.map(arg => {
      if (typeof arg === 'string') return arg;
      if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    }).join(' ');

    const entry: LogEntry = {
      id: `log-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      level,
      message,
      args,
    };

    this.logs.push(entry);

    // Keep only last maxLogs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Notify listeners
    this.listeners.forEach(listener => listener([...this.logs]));
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  subscribe(listener: (logs: LogEntry[]) => void): () => void {
    this.listeners.add(listener);
    // Immediately call with current logs
    listener([...this.logs]);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  clear() {
    this.logs = [];
    this.listeners.forEach(listener => listener([]));
  }

  // Restore original console (for cleanup)
  restore() {
    console.log = this.originalConsole.log;
    console.error = this.originalConsole.error;
    console.warn = this.originalConsole.warn;
  }
}

// Singleton instance
export const logCapture = new LogCaptureService();
